import { useEffect } from 'react';
import noop from 'lodash-es/noop';
import { useCustomPreferenceOrDefault } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import {
    cleanEffectiveDateTime,
    normalizeEffectiveDateTime,
    setPreviewDateInUrl,
} from '../../utils/preview-mode-utils';
import { useCustomerContextGet } from './useCustomerContextGet';
import { useCustomerUpdateContext } from './useCustomerUpdateContext';
import { useServerEffectiveDateTime } from './useServerEffectiveDateTime';

export const useCustomerContextFixer = (): void => {
    const serverEffectiveDateTime = useServerEffectiveDateTime();
    const customerContext = useCustomerContextGet();
    const updateContext = useCustomerUpdateContext();
    const pwaSurlatablePreview = useCustomPreferenceOrDefault(
        CustomPreference.PWA_SURLATABLE_PREVIEW,
        false,
    );

    useEffect((): void => {
        if (
            !pwaSurlatablePreview ||
            updateContext.isLoading ||
            customerContext.isLoading ||
            typeof customerContext.data !== 'object'
        ) {
            return;
        }
        const contextEffectiveDateTime = cleanEffectiveDateTime(
            customerContext.data.effectiveDateTime,
        );
        // only if the server effective date time is set and the context effective date time is not the same
        if (serverEffectiveDateTime && contextEffectiveDateTime !== serverEffectiveDateTime) {
            // we know this mutate will not trigger any reload, since it matches the URL date
            updateContext
                .mutateAsync({
                    effectiveDateTime: normalizeEffectiveDateTime(serverEffectiveDateTime),
                })
                .then(() => {
                    // reload the page to apply the new effective date time
                    window.location.reload();
                })
                .catch(noop);
        } else if (!serverEffectiveDateTime && contextEffectiveDateTime) {
            // update the URL from the context effective date time
            setPreviewDateInUrl(contextEffectiveDateTime);
            // no need to reload because setPreviewDateInUrl will do it.
        }
    }, [
        customerContext.data,
        serverEffectiveDateTime,
        updateContext,
        pwaSurlatablePreview,
        customerContext.isLoading,
    ]);
};
